import { AxiosError } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { API_ENDPOINTS, buildApiUrl } from '@/constants';
import { api } from '@/utils';

import type { Campaign } from '@/components/campaigns/types';

interface UseFetchCampaignsForCallsOptions {
    search?: string;
    limit?: number;
}

/**
 * Custom hook to fetch campaigns for the call listing.
 *
 * @param {string} options.search - Search term to filter campaigns by name
 * @param {number} options.limit - Amount of campaigns to return
 */
const useFetchCampaignsForCalls = (options: UseFetchCampaignsForCallsOptions = {}) => {
    const { search, limit } = options;

    const { data, isLoading, isError, error } = useQuery<Campaign[], AxiosError>({
        queryKey: ['campaigns-for-calls', search, limit],
        queryFn: async () => {
            const params: Record<string, string> = {};

            if (search && search.trim().length >= 2) {
                params.search = search.trim();
            }

            if (limit && limit > 0) {
                params.limit = limit.toString();
            }

            const url = buildApiUrl(API_ENDPOINTS.CAMPAIGNS_FOR_CALLS, params);
            const response = await api.get(url);

            return response.data;
        },
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        retry: 2,
    });

    return {
        data,
        isLoading,
        isError,
        error,
    };
};

export default useFetchCampaignsForCalls;
